import { Boxes, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { isApiError } from '@/lib/api/errors'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { useProjects } from './api'
import { useCatalog, useCatalogModule, useDeleteCatalogModule } from './catalogApi'
import { CatalogModuleFormModal } from './components/catalog/CatalogModuleFormModal'
import { IntegrateModal, type IntegrateTarget } from './components/catalog/IntegrateModal'
import { ModuleChangelog } from './components/catalog/ModuleChangelog'
import { ModuleFeatures } from './components/catalog/ModuleFeatures'
import { ModuleInfoAside } from './components/catalog/ModuleInfoAside'
import { ModuleProjects } from './components/catalog/ModuleProjects'
import { PROJECT_PRICING_PERM } from './permissions'
import type { CatalogModuleView } from './types'
import { toastError } from './ui'
import { MODULE_CATEGORY_ICON, MODULE_CATEGORY_TONE } from './visuals'

export function ModuleDetailPage() {
  const { t } = useTranslation()
  const { moduleId = '' } = useParams()
  const module = useCatalogModule(moduleId)

  if (module.isError) {
    return isApiError(module.error) && module.error.status === 404 ? (
      <EmptyState icon={Boxes} title={t('projects.catalog.detail.notFound')} />
    ) : (
      <ErrorState error={module.error} onRetry={() => void module.refetch()} />
    )
  }
  if (!module.data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    )
  }
  return <ModuleDetail module={module.data} />
}

function ModuleDetail({ module: m }: { module: CatalogModuleView }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const navigate = useNavigate()
  const catalog = useCatalog()
  const projects = useProjects()
  const remove = useDeleteCatalogModule()
  const [editing, setEditing] = useState(false)
  const [target, setTarget] = useState<IntegrateTarget | null>(null)
  const canWrite = can(PROJECT_PRICING_PERM)
  const Icon = MODULE_CATEGORY_ICON[m.category]
  const all = catalog.data ?? []
  const projectList = projects.data ?? []
  const canAdd = m.active && projectList.some((p) => !m.usage.some((u) => u.projectId === p.id))

  const onDelete = async () => {
    const ok = await confirmModal({ title: t('projects.catalog.deleteTitle'), description: t('projects.catalog.deleteBody', { name: m.name }), confirmLabel: t('common.delete'), destructive: true })
    if (!ok) return
    try {
      await remove.mutateAsync(m.id)
      toast.success(t('projects.catalog.deleted'))
      navigate('/projects/catalog', { replace: true })
    } catch (e) {
      const details = isApiError(e) ? (e.details as { projects?: string[]; modules?: string[] } | undefined) : undefined
      const names = details?.projects ?? details?.modules
      if (isApiError(e) && names) toast.error(t(`errors.${e.code}`), { description: names.join(', ') })
      else toastError(e)
    }
  }

  return (
    <>
      <PageHeader
        backTo="/projects/catalog"
        eyebrow={
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono">{m.code}</span>
            <Badge tone="outline" className="rounded-md">{t(`projects.moduleCategory.${m.category}`)}</Badge>
            <Badge tone="primary" className="rounded-md">v{m.version}</Badge>
            <Badge tone={m.active ? 'success' : 'neutral'} className="rounded-md">{t(m.active ? 'projects.catalog.activeLabel' : 'projects.catalog.passive')}</Badge>
          </span>
        }
        title={
          <span className="flex items-center gap-3">
            <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg', MODULE_CATEGORY_TONE[m.category])}>
              <Icon className="size-5" aria-hidden />
            </span>
            {m.name}
          </span>
        }
        description={m.description}
        actions={
          canWrite ? (
            <>
              {canAdd ? (
                <Button onClick={() => setTarget({ kind: 'create', moduleId: m.id })}>
                  <Plus />
                  {t('projects.catalog.addToProject')}
                </Button>
              ) : null}
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil />
                {t('common.edit')}
              </Button>
              <Button variant="outline" size="icon" className="hover:text-destructive" onClick={() => void onDelete()} aria-label={t('common.delete')}>
                <Trash2 />
              </Button>
            </>
          ) : null
        }
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-4">
          <ModuleFeatures module={m} canWrite={canWrite} />
          <ModuleProjects module={m} projects={projectList} canWrite={canWrite} onIntegrate={setTarget} />
          <ModuleChangelog module={m} canWrite={canWrite} />
        </div>
        <ModuleInfoAside module={m} catalog={all} />
      </div>

      <CatalogModuleFormModal open={editing} module={m} catalog={all} onOpenChange={setEditing} />
      <IntegrateModal target={target} catalog={all} projects={projectList} onClose={() => setTarget(null)} />
    </>
  )
}
